// Public, token-secured recipient page for a brand installment plan.
// No auth required — the URL must include ?t=<public_token>.

import * as React from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { z } from "zod";

const Search = z.object({
  t: z.string().min(16).max(80).optional(),
  pf: z.enum(["return", "cancel"]).optional(),
});

type PlanView = {
  brand_name: string | null;
  reference: string;
  status: string;
  total_amount_cents: number;
  weekly_amount_cents: number;
  paid_amount_cents: number;
  next_due_date: string | null;
  final_due_date: string | null;
  total_weeks: number | null;
  recipient_name: string | null;
  recipient_email: string;
  payfast_enabled: boolean;
  invoice_url?: string | null;
  payments: Array<{ amount_cents: number; method: string; paid_at: string }>;
  schedule: Array<{ index: number; due_date: string; amount_cents: number; balance_after_cents: number }>;
};

function zar(c: number) { return `R${(c / 100).toFixed(2)}`; }

export const Route = createFileRoute("/installments/$ref")({
  validateSearch: (s) => Search.parse(s),
  component: PublicInstallmentPage,
});

function PublicInstallmentPage() {
  const { ref } = Route.useParams() as { ref: string };
  const search = useSearch({ from: "/installments/$ref" });
  const token = search.t ?? "";

  const [plan, setPlan] = React.useState<PlanView | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState<string | null>(null);

  const [payAmount, setPayAmount] = React.useState<number>(50);
  const [payerEmail, setPayerEmail] = React.useState("");
  const [payerName, setPayerName] = React.useState("");
  const [claimNote, setClaimNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState<null | "pay" | "claim">(null);
  const [flash, setFlash] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const url = new URL("/api/public/brand-installments/lookup", window.location.origin);
      url.searchParams.set("ref", ref);
      url.searchParams.set("t", token);
      const r = await fetch(url.toString(), { method: "GET" });
      const json = await r.json();
      if (!r.ok || !json?.ok) throw new Error(json?.error ?? "lookup_failed");
      setPlan(json.plan);
      const remaining = (json.plan.total_amount_cents - json.plan.paid_amount_cents) / 100;
      setPayAmount(Math.min(Math.max(10, json.plan.weekly_amount_cents / 100), Math.max(10, remaining)));
      if (json.plan.recipient_name) setPayerName(json.plan.recipient_name);
      if (json.plan.recipient_email) setPayerEmail(json.plan.recipient_email);
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, [ref, token]);

  React.useEffect(() => { if (token) void load(); }, [load, token]);

  async function handlePay(e: React.FormEvent) {
    e.preventDefault();
    if (!plan) return;
    setSubmitting("pay");
    setFlash(null);
    try {
      const r = await fetch("/api/public/brand-installments?action=pay", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          token,
          reference: ref,
          amount_zar: payAmount,
          payer_email: payerEmail,
          payer_name: payerName,
        }),
      });
      const json = await r.json();
      if (!r.ok || !json?.ok) throw new Error(json?.error ?? "pay_failed");
      // Build and submit form to PayFast
      const form = document.createElement("form");
      form.method = "POST";
      form.action = json.processUrl;
      for (const [k, v] of Object.entries(json.fields as Record<string, string>)) {
        const i = document.createElement("input");
        i.type = "hidden"; i.name = k; i.value = String(v);
        form.appendChild(i);
      }
      document.body.appendChild(form);
      form.submit();
    } catch (e: any) {
      setFlash(`Payment error: ${e?.message ?? e}`);
      setSubmitting(null);
    }
  }

  async function handleClaim(e: React.FormEvent) {
    e.preventDefault();
    if (!plan) return;
    setSubmitting("claim");
    setFlash(null);
    try {
      const r = await fetch("/api/public/brand-installments?action=claim", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          token,
          reference: ref,
          amount_zar: payAmount,
          payer_email: payerEmail,
          note: claimNote,
        }),
      });
      const json = await r.json();
      if (!r.ok || !json?.ok) throw new Error(json?.error ?? "claim_failed");
      setFlash("Thank you — your payment proof was submitted. You will receive an email once it is verified.");
      setClaimNote("");
      await load();
    } catch (e: any) {
      setFlash(`Submission error: ${e?.message ?? e}`);
    } finally {
      setSubmitting(null);
    }
  }

  if (!token) {
    return (
      <Wrap>
        <h1 className="text-xl font-semibold text-[#1F3A5F]">Invalid link</h1>
        <p className="mt-2 text-sm text-gray-600">
          This payment page requires the secure token from your invoice email.
          Please open the link from your inbox.
        </p>
      </Wrap>
    );
  }

  if (loading) return <Wrap><p className="text-sm text-gray-500">Loading…</p></Wrap>;
  if (err || !plan) return <Wrap><p className="text-sm text-red-600">Could not load this invoice: {err ?? "not found"}</p></Wrap>;

  const remainingCents = Math.max(0, plan.total_amount_cents - plan.paid_amount_cents);
  const settled = plan.status === "settled" || remainingCents === 0;

  return (
    <Wrap>
      <header className="border-b-2 border-[#C7A24A] pb-3 mb-5">
        <div className="text-[11px] uppercase tracking-widest text-[#1F3A5F]">
          {plan.brand_name ?? "Invoice"}
        </div>
        <h1 className="text-2xl font-semibold text-[#1F3A5F]">Invoice {plan.reference}</h1>
      </header>

      {search.pf === "return" && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Thanks — your payment was submitted to PayFast. The balance below updates automatically once we receive confirmation.
        </div>
      )}
      {search.pf === "cancel" && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The payment was cancelled. You can try again below.
        </div>
      )}
      {flash && (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          {flash}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Card label="Total due" value={zar(plan.total_amount_cents)} />
        <Card label="Paid so far" value={zar(plan.paid_amount_cents)} />
        <Card label="Remaining balance" value={zar(remainingCents)} highlight />
        <Card label="Weekly instalment" value={`${zar(plan.weekly_amount_cents)} (min R50.00)`} />
        <Card label="Next due date" value={plan.next_due_date ? new Date(plan.next_due_date).toLocaleDateString("en-ZA") : "—"} />
        <Card label="Final payment date" value={plan.final_due_date ? new Date(plan.final_due_date).toLocaleDateString("en-ZA") : "—"} />
      </div>

      {plan.invoice_url && (
        <p className="mt-4 text-sm">
          <a className="text-[#1F3A5F] underline" href={plan.invoice_url}>Download invoice PDF</a>
        </p>
      )}

      {settled ? (
        <div className="mt-6 rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900">
          This invoice is fully settled. Thank you!
        </div>
      ) : (
        <>
          <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold text-[#1F3A5F]">Pay an instalment</h2>
            <p className="mt-1 text-xs text-gray-500">
              Pay any amount from R10.00 up to your remaining balance of {zar(remainingCents)}. Your balance updates automatically.
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {[50, 100, 200, 500, remainingCents / 100].map((v, i) => {
                const amt = Math.max(10, Math.min(remainingCents / 100, Math.round(v)));
                const label = i === 4 ? `Pay full ${zar(remainingCents)}` : `R${amt}`;
                return (
                  <button key={i} type="button" onClick={() => setPayAmount(amt)}
                    className="rounded-full border border-[#1F3A5F]/30 px-3 py-1 text-xs hover:bg-[#1F3A5F]/5">
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Your name">
                <input className="input" value={payerName} onChange={(e) => setPayerName(e.target.value)} maxLength={120} />
              </Field>
              <Field label="Your email" required>
                <input className="input" type="email" value={payerEmail} onChange={(e) => setPayerEmail(e.target.value)} maxLength={254} required />
              </Field>
              <Field label="Amount (R)" required>
                <input
                  className="input"
                  type="number" min={10} max={remainingCents / 100} step="0.01"
                  value={payAmount}
                  onChange={(e) => setPayAmount(Math.max(10, Math.min(remainingCents / 100, Number(e.target.value) || 0)))}
                />
              </Field>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              {plan.payfast_enabled && (
                <form onSubmit={handlePay}>
                  <button
                    type="submit"
                    disabled={submitting !== null || payAmount < 10 || !payerEmail}
                    className="rounded-md bg-[#1F3A5F] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {submitting === "pay" ? "Redirecting…" : `Pay ${zar(Math.round(payAmount * 100))} with PayFast`}
                  </button>
                </form>
              )}
              <form onSubmit={handleClaim} className="flex-1 min-w-[260px]">
                <textarea
                  className="input min-h-[60px]"
                  placeholder="Optional: EFT reference, date, or note — if you have already paid by EFT, submit this so we can verify it."
                  value={claimNote}
                  onChange={(e) => setClaimNote(e.target.value.slice(0, 500))}
                />
                <button
                  type="submit"
                  disabled={submitting !== null || payAmount < 10 || !payerEmail}
                  className="mt-2 rounded-md border border-[#1F3A5F] px-4 py-2 text-sm font-medium text-[#1F3A5F] disabled:opacity-50"
                >
                  {submitting === "claim" ? "Submitting…" : "I paid by EFT — submit proof"}
                </button>
              </form>
            </div>
          </section>
        </>
      )}

      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-base font-semibold text-[#1F3A5F]">Weekly schedule</h2>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-1 pr-2">#</th>
                <th className="py-1 pr-2">Due date</th>
                <th className="py-1 pr-2 text-right">Amount</th>
                <th className="py-1 pr-2 text-right">Balance after</th>
              </tr>
            </thead>
            <tbody>
              {plan.schedule.map((row) => (
                <tr key={row.index} className="border-b last:border-0">
                  <td className="py-1 pr-2">{row.index}</td>
                  <td className="py-1 pr-2">{new Date(row.due_date).toLocaleDateString("en-ZA")}</td>
                  <td className="py-1 pr-2 text-right">{zar(row.amount_cents)}</td>
                  <td className="py-1 pr-2 text-right">{zar(row.balance_after_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-base font-semibold text-[#1F3A5F]">Payment history</h2>
        {plan.payments.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No payments recorded yet.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 text-sm">
            {plan.payments.map((p, i) => (
              <li key={i} className="flex justify-between py-1">
                <span>{new Date(p.paid_at).toLocaleString("en-ZA")} · {p.method.toUpperCase()}</span>
                <span className="font-medium">{zar(p.amount_cents)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <style>{`.input{width:100%;border:1px solid #D1D5DB;border-radius:6px;padding:8px 10px;font-size:14px;background:#fff;color:#111}`}</style>
    </Wrap>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#F7F4EC] py-8">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="rounded-lg border border-gray-200 bg-white p-5 sm:p-7 shadow-sm">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600">{label}{required && <span className="text-red-500"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Card({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${highlight ? "border-[#E7C96A] bg-[#FFF8E7]" : "border-gray-200 bg-gray-50"}`}>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 text-base font-semibold text-[#1F3A5F]">{value}</div>
    </div>
  );
}
